import { useEffect, useReducer } from 'react'

import { hotelStatusNamesFilterable } from 'main/hotelStatuses'

const defaultLocalStorageKey = 'daily-conversations-filters'

const allSubsidiaryStatuses = Object.keys(hotelStatusNamesFilterable)
const allStayPeriods = ['before', 'during', 'after']

/**
 * Initializes the state for the daily conversations filters.
 * Tries to retrieve from localStorage, falling back to default values.
 * Default: all multi-select filters are marked (all selected). When zeroed, show only items without values.
 *
 * @param {string} key - The unique key for storing the filter state in localStorage.
 * @returns {Object} The initial state for the filter.
 */
const getInitialState = key => {
  const savedState = localStorage.getItem(key)
  const defaultState = {
    hotelNameSearch: '',
    selectedBotContexts: [],
    selectedBotContextsHasBeenModified: false,
    showUnreviewedOnly: false,
    botIntentFilter: 'all',
    isFilterPanelOpen: true,
    sortBy: 'hotelNameAsc',
    hotelGroups: 'all',
    chains: 'all',
    brands: 'all',
    subsidiaryStatus: [...allSubsidiaryStatuses],
    justShowFlagged: false,
    stayPeriodFilter: [...allStayPeriods]
  }

  if (savedState) {
    const parsed = JSON.parse(savedState)
    // Migrate old string format to array format
    if (parsed.stayPeriodFilter && typeof parsed.stayPeriodFilter === 'string') {
      parsed.stayPeriodFilter = parsed.stayPeriodFilter === 'all' ? [] : [parsed.stayPeriodFilter]
    }
    if (parsed.selectedBotContextsHasBeenModified === undefined) {
      parsed.selectedBotContextsHasBeenModified = false
    }
    return { ...defaultState, ...parsed }
  }

  return defaultState
}

function filterReducer(state, action) {
  switch (action.type) {
    case 'hotelNameSearch':
      return { ...state, hotelNameSearch: action.payload }
    case 'selectedBotContexts':
      return {
        ...state,
        selectedBotContexts: action.payload,
        selectedBotContextsHasBeenModified: true
      }
    case 'showUnreviewedOnly':
      return { ...state, showUnreviewedOnly: action.payload }
    case 'botIntentFilter':
      return { ...state, botIntentFilter: action.payload }
    case 'isFilterPanelOpen':
      return { ...state, isFilterPanelOpen: action.payload }
    case 'sortBy':
      return { ...state, sortBy: action.payload }
    case 'hotelGroups':
      return { ...state, hotelGroups: action.payload }
    case 'chains':
      return { ...state, chains: action.payload }
    case 'brands':
      return { ...state, brands: action.payload }
    case 'subsidiaryStatus':
      return { ...state, subsidiaryStatus: action.payload }
    case 'justShowFlagged':
      return { ...state, justShowFlagged: action.payload }
    case 'stayPeriodFilter':
      return { ...state, stayPeriodFilter: action.payload }
    default:
      throw new Error()
  }
}

/**
 * Custom hook for managing and persisting daily conversations filters.
 *
 * @returns {Array} State and dispatch method for the filters.
 */
function useDailyConversationsFilters() {
  const storageKey = defaultLocalStorageKey
  const [state, dispatch] = useReducer(filterReducer, getInitialState(storageKey))

  // Save to localStorage whenever the state changes
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(state))
  }, [state, storageKey])

  return [state, dispatch]
}

export default useDailyConversationsFilters

